/**
 * CalendarBookingsPage
 *
 * Calendar view for managing bookings per agent.
 * Allows operators to list, create, edit, and cancel bookings,
 * with filters by date range, resource, and status.
 * Includes changelog viewer for booking history.
 *
 * Validates: Requirements 9.1, 9.2, 9.3, 9.4, 9.5, 9.6
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Badge, Button, Input, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import { resolveWidgetMediaUrl } from '../../lib/resolveWidgetMediaUrl';
import { bookingChatPath } from './calendarView/bookingChatLink';
import { airbnbStayKind, airbnbStayShortLabel } from './calendarView.utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ResourceInfo {
  id: string;
  name: string;
  resourceType: {
    id: string;
    name: string;
    bookingMode: string;
    cancellationPolicy: any;
    bookingStatusSettings?: unknown | null;
  };
}

interface ChangelogEntry {
  action: 'created' | 'modified' | 'cancelled' | 'auto_cancelled' | 'confirmed' | 'receipt_attached';
  timestamp: string;
  operatorId?: string;
  reason?: string;
  changes?: Record<string, { from: any; to: any }>;
}

interface Booking {
  id: string;
  resourceId: string;
  virtualAgentId: string;
  conversationId: string | null;
  sourceMessageId?: string | null;
  sourceMessage?: { id: string; content: string; sender: string } | null;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
  startDate: string;
  endDate: string;
  timeBlock: string | null;
  status: string;
  cancellationReason: string | null;
  createdBy: string;
  changelog: ChangelogEntry[];
  createdAt: string;
  updatedAt: string;
  /** Fin de la hora de seña. Null si esta reserva no vence sola. */
  depositExpiresAt?: string | null;
  resource?: ResourceInfo;
  depositReceiptUrl?: string | null;
  /** Código público al confirmar (3 letras + 3 dígitos). */
  publicReference?: string | null;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
}

interface BookingListResult {
  bookings: Booking[];
  total: number;
  page: number;
  pageSize: number;
}

interface ResourceOption {
  id: string;
  name: string;
  resourceType?: ResourceInfo['resourceType'];
}

interface BookingFormData {
  resourceId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  startDate: string;
  endDate: string;
  timeBlock: string;
  status: 'pending_deposit' | 'confirmed' | 'human_supervision';
  createdBy: 'visitor' | 'operator';
}

const EMPTY_FORM: BookingFormData = {
  resourceId: '',
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  startDate: '',
  endDate: '',
  timeBlock: '',
  status: 'pending_deposit',
  createdBy: 'operator',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  pending_deposit: 'Pendiente de seña',
  confirmed: 'Confirmada',
  human_supervision: 'Supervisión humana',
  cancelled: 'Cancelada',
  auto_cancelled: 'Cancelada automáticamente',
  completed: 'Completada (hist.)',
};

function statusBadgeVariant(status: string): 'warning' | 'success' | 'error' | 'info' | 'neutral' {
  switch (status) {
    case 'pending': return 'warning';
    case 'pending_deposit': return 'warning';
    case 'confirmed': return 'success';
    case 'human_supervision': return 'info';
    case 'completed': return 'neutral';
    case 'cancelled': return 'error';
    case 'auto_cancelled': return 'neutral';
    default: return 'neutral';
  }
}

type OperatorBookingStatus = BookingFormData['status'];

const ALL_OPERATOR_STATUSES: OperatorBookingStatus[] = ['pending_deposit', 'confirmed', 'human_supervision'];

function getEnabledOperatorStatusesFromType(raw: unknown): OperatorBookingStatus[] | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const es = (raw as { enabledStatuses?: unknown }).enabledStatuses;
  if (!Array.isArray(es) || es.length === 0) return null;
  const allow = new Set<string>(ALL_OPERATOR_STATUSES);
  const out = es.filter((s): s is OperatorBookingStatus => typeof s === 'string' && allow.has(s));
  return out.length ? out : null;
}

function operatorStatusChoicesForResource(
  resource?: { resourceType?: { bookingStatusSettings?: unknown } } | null
): OperatorBookingStatus[] {
  const enabled = getEnabledOperatorStatusesFromType(resource?.resourceType?.bookingStatusSettings);
  if (enabled) return enabled;
  return [...ALL_OPERATOR_STATUSES];
}

function mergeCurrentOperatorStatus(
  choices: OperatorBookingStatus[],
  current: OperatorBookingStatus
): OperatorBookingStatus[] {
  if (choices.includes(current)) return choices;
  return [...choices, current];
}

/** Etiqueta de estado: prioriza textos del tipo de recurso, luego catálogo fijo. */
function statusDisplayLabel(
  status: string,
  resource?: { resourceType?: ResourceInfo['resourceType'] } | null
): string {
  const raw = resource?.resourceType?.bookingStatusSettings;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const labels = (raw as { labels?: unknown }).labels;
    if (labels && typeof labels === 'object' && !Array.isArray(labels)) {
      const v = (labels as Record<string, unknown>)[status];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return STATUS_LABELS[status] || status;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconCalendar() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
      <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconEye() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="15 18 9 12 15 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="9 18 15 12 9 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function apiStatusToFormStatus(status: string): BookingFormData['status'] {
  if (status === 'confirmed') return 'confirmed';
  if (status === 'human_supervision') return 'human_supervision';
  if (status === 'pending_deposit' || status === 'pending') return 'pending_deposit';
  if (status === 'completed') return 'human_supervision';
  return 'pending_deposit';
}

function chatMessagePreview(content: string | null | undefined): string | null {
  const text = (content ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  return text.length <= 140 ? text : `${text.slice(0, 139)}…`;
}

function formatDateTime(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Convierte ISO UTC al valor local esperado por input[type="datetime-local"]. */
function isoToLocalDateTimeInput(isoStr: string): string {
  const d = new Date(isoStr);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateShort(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Custom DELETE with body (apiDelete doesn't support body) */
async function apiDeleteWithBody<T>(
  path: string,
  body: unknown,
  token: string
): Promise<T> {
  const r = await fetch(path, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const j = await r.json();
  if (!r.ok || !j.success) {
    throw new Error(j.error?.message || `HTTP ${r.status}`);
  }
  return j.data as T;
}

/** Tiempo que le queda a la seña. Null si esta reserva no vence sola. */
function getCountdownText(booking: Pick<Booking, 'status' | 'createdAt' | 'depositExpiresAt'>, now = Date.now()): string | null {
  if (booking.status !== 'pending_deposit') return null;
  if (booking.depositExpiresAt === null) return null;
  const expiresAt = booking.depositExpiresAt
    ? new Date(booking.depositExpiresAt).getTime()
    : new Date(booking.createdAt).getTime() + 60 * 60 * 1000;
  if (Number.isNaN(expiresAt)) return null;
  const remainingMs = expiresAt - now;
  if (remainingMs <= 0) return 'Seña vencida';
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const clock =
    hours > 0
      ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `Quedan ${clock}`;
}

function depositReceiptResolvedHref(raw: string): string {
  const t = raw.trim();
  if (!t || t.startsWith('data:')) return t;
  return resolveWidgetMediaUrl(t);
}

function depositReceiptLooksLikeImage(raw: string): boolean {
  const t = raw.trim();
  if (t.startsWith('data:image/')) return true;
  if (t.startsWith('data:')) return false;
  try {
    const pathOnly = /^https?:\/\//i.test(t)
      ? new URL(t).pathname
      : (t.split('?')[0]?.split('#')[0] ?? t);
    return /\.(jpe?g|png|gif|webp)$/i.test(pathOnly);
  } catch {
    return /\.(jpe?g|png|gif|webp)(\?|$)/i.test(t);
  }
}

function depositReceiptLooksLikePdf(raw: string): boolean {
  const t = raw.trim();
  if (/^data:application\/pdf/i.test(t)) return true;
  try {
    const pathOnly = /^https?:\/\//i.test(t)
      ? new URL(t).pathname
      : (t.split('?')[0]?.split('#')[0] ?? t);
    return /\.pdf$/i.test(pathOnly);
  } catch {
    return /\.pdf(\?|$)/i.test(t);
  }
}

function depositReceiptDataDownloadName(raw: string): string {
  const m = raw.match(/^data:([^;,]+)/i);
  const mime = (m?.[1] ?? '').toLowerCase();
  if (mime.includes('pdf')) return 'comprobante-seña.pdf';
  if (mime.includes('png')) return 'comprobante-seña.png';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'comprobante-seña.jpg';
  if (mime.includes('gif')) return 'comprobante-seña.gif';
  if (mime.includes('webp')) return 'comprobante-seña.webp';
  return 'comprobante-seña';
}

function BookingDepositReceiptBlock({ rawUrl }: { rawUrl: string }) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const raw = rawUrl.trim();
  const href = depositReceiptResolvedHref(raw);
  const isData = raw.startsWith('data:');
  const isImg = depositReceiptLooksLikeImage(raw);
  const isPdf = depositReceiptLooksLikePdf(raw);
  /** data: no imagen salvo PDF → solo descarga (iframe suele fallar). */
  const showEmbedPreview = isImg || !isData || isPdf;

  const thumbStyle = {
    maxWidth: 200,
    maxHeight: 150,
    borderRadius: tokens.borderRadius.md,
    border: `1px solid ${tokens.colors.neutral[200]}`,
    objectFit: 'cover' as const,
    cursor: 'pointer' as const,
    display: 'block' as const,
  };
  const linkStyle = {
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    gap: 6,
    padding: '6px 12px',
    fontSize: 13,
    color: tokens.colors.primary[600],
    backgroundColor: tokens.colors.primary[50],
    borderRadius: tokens.borderRadius.md,
    textDecoration: 'none' as const,
    border: `1px solid ${tokens.colors.primary[100]}`,
    cursor: 'pointer' as const,
    fontFamily: 'inherit',
  };
  const thumbButtonStyle = {
    border: 'none',
    padding: 0,
    margin: 0,
    background: 'transparent',
    cursor: 'pointer' as const,
    borderRadius: tokens.borderRadius.md,
    display: 'inline-block' as const,
  };

  const imagePreviewSrc = isData ? raw : href;
  const embedSrc = isData ? raw : href;

  return (
    <>
      <div style={{ gridColumn: '1 / -1' }}>
        <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Comprobante de seña</span>
        <div style={{ marginTop: 4 }}>
          {isImg ? (
            <button
              type="button"
              onClick={() => setViewerOpen(true)}
              aria-label="Ver comprobante en grande"
              style={thumbButtonStyle}
            >
              <img
                src={imagePreviewSrc}
                alt="Comprobante de seña"
                title="Clic para ver en grande"
                style={thumbStyle}
              />
            </button>
          ) : showEmbedPreview ? (
            <button type="button" onClick={() => setViewerOpen(true)} style={linkStyle}>
              📎 Ver comprobante
            </button>
          ) : (
            <a href={raw} download={depositReceiptDataDownloadName(raw)} style={linkStyle}>
              📎 Descargar comprobante
            </a>
          )}
        </div>
      </div>

      <Modal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title="Comprobante de seña"
        size="full"
        showCloseButton
        closeOnOverlayClick
        closeOnEscape
        footer={
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: tokens.spacing[3],
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            {isData ? (
              <a
                href={raw}
                download={depositReceiptDataDownloadName(raw)}
                style={{ fontSize: 13, color: tokens.colors.primary[600], fontWeight: 600 }}
              >
                Descargar
              </a>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 13, color: tokens.colors.primary[600], fontWeight: 600 }}
              >
                Abrir en pestaña nueva
              </a>
            )}
            <Button variant="primary" onClick={() => setViewerOpen(false)}>
              Cerrar
            </Button>
          </div>
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 240,
            width: '100%',
          }}
        >
          {isImg ? (
            <img
              src={imagePreviewSrc}
              alt=""
              style={{
                maxWidth: '100%',
                maxHeight: 'min(78vh, 900px)',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: tokens.borderRadius.md,
              }}
            />
          ) : (
            <iframe
              title="Comprobante"
              src={embedSrc}
              style={{
                width: '100%',
                minHeight: 'min(75vh, 720px)',
                height: '75vh',
                border: 0,
                borderRadius: tokens.borderRadius.md,
                background: '#fff',
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

function airbnbNameInput(booking: Pick<Booking, 'customerName' | 'createdBy'>) {
  const fromIcal = booking.createdBy === 'ical_import';
  return {
    customerName: booking.customerName,
    createdBy: booking.createdBy,
    bookingOrigin: fromIcal ? ('ota' as const) : undefined,
    otaChannel: fromIcal ? 'airbnb' : undefined,
  };
}

function airbnbNameLabel(booking: Pick<Booking, 'customerName' | 'createdBy'>): string {
  return airbnbStayShortLabel(airbnbNameInput(booking)) ?? booking.customerName;
}

function airbnbNameWeight(booking: Pick<Booking, 'customerName' | 'createdBy'>): number {
  const kind = airbnbStayKind(airbnbNameInput(booking));
  if (kind === 'reserved') return 800;
  if (kind === 'block') return 400;
  return 600;
}

export function CalendarBookingsPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  const highlightBookingId = searchParams.get('bookingId');
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  // List state
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Resources for dropdown
  const [resources, setResources] = useState<ResourceOption[]>([]);

  // Filters
  const [filterResourceId, setFilterResourceId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BookingFormData>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Cancel modal
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Detail/Changelog modal
  const [detailBooking, setDetailBooking] = useState<Booking | null>(null);

  // El contador de la seña se actualiza cada segundo mientras haya una reserva pendiente.
  const [, setTick] = useState(0);
  useEffect(() => {
    const hasPendingDeposit = bookings.some((b) => b.status === 'pending_deposit') || detailBooking?.status === 'pending_deposit';
    if (!hasPendingDeposit) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [bookings, detailBooking?.status]);

  useEffect(() => {
    if (!highlightBookingId || bookings.length === 0) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById(`booking-row-${highlightBookingId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(t);
  }, [highlightBookingId, bookings]);

  const resourceForStatusLabels = useMemo((): ResourceInfo | null => {
    if (editingId) {
      const b = bookings.find((x) => x.id === editingId);
      return b?.resource ?? null;
    }
    const r = resources.find((x) => x.id === form.resourceId);
    if (!r?.resourceType) return null;
    return {
      id: r.id,
      name: r.name,
      resourceType: r.resourceType,
    };
  }, [editingId, bookings, form.resourceId, resources]);

  const statusModalChoices = useMemo((): OperatorBookingStatus[] => {
    let base: OperatorBookingStatus[];
    if (editingId) {
      base = operatorStatusChoicesForResource(bookings.find((b) => b.id === editingId)?.resource ?? null);
    } else {
      base = operatorStatusChoicesForResource(resources.find((r) => r.id === form.resourceId) ?? null);
    }
    return mergeCurrentOperatorStatus(base, form.status);
  }, [editingId, bookings, form.resourceId, form.status, resources]);

  useEffect(() => {
    if (!modalOpen || editingId) return;
    const r = resources.find((x) => x.id === form.resourceId);
    const choices = operatorStatusChoicesForResource(r ?? null);
    setForm((f) => {
      if (choices.includes(f.status)) return f;
      return { ...f, status: choices[0] ?? 'pending_deposit' };
    });
  }, [modalOpen, editingId, form.resourceId, resources]);

  // ─── Data Loading ────────────────────────────────────────────────────────

  const loadResources = useCallback(async () => {
    if (!agentId || !token) return;
    try {
      const data = await apiGet<ResourceOption[]>(
        `/api/agents/${agentId}/calendar/resources`,
        token
      );
      setResources(data);
    } catch {
      // Non-critical
    }
  }, [agentId, token]);

  const loadBookings = useCallback(async () => {
    if (!agentId || !token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterResourceId) params.set('resourceId', filterResourceId);
      if (filterStatus) params.set('status', filterStatus);
      if (filterStartDate) params.set('startDate', new Date(filterStartDate).toISOString());
      if (filterEndDate) params.set('endDate', new Date(filterEndDate).toISOString());
      const qSearch = filterSearch.trim();
      if (qSearch) params.set('search', qSearch);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      const qs = params.toString();
      const url = `/api/agents/${agentId}/calendar/bookings?${qs}`;
      const data = await apiGet<BookingListResult>(url, token);
      setBookings(data.bookings);
      setTotal(data.total);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentId, token, filterResourceId, filterStatus, filterStartDate, filterEndDate, filterSearch, page, pageSize]);

  useEffect(() => {
    void loadResources();
  }, [loadResources]);

  useEffect(() => {
    void loadBookings();
  }, [loadBookings]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filterResourceId, filterStatus, filterStartDate, filterEndDate, filterSearch]);

  // ─── Form Handlers ───────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setModalOpen(true);
  };

  const openEditModal = (booking: Booking) => {
    setEditingId(booking.id);
    setForm({
      resourceId: booking.resourceId,
      customerName: booking.customerName,
      customerEmail: booking.customerEmail || '',
      customerPhone: booking.customerPhone || '',
      startDate: isoToLocalDateTimeInput(booking.startDate),
      endDate: isoToLocalDateTimeInput(booking.endDate),
      timeBlock: booking.timeBlock || '',
      status: apiStatusToFormStatus(booking.status),
      createdBy: booking.createdBy as 'visitor' | 'operator',
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.resourceId) errors.resourceId = 'Debe seleccionar un recurso';
    if (!form.customerName.trim()) errors.customerName = 'El nombre del cliente es requerido';
    if (!form.startDate) errors.startDate = 'La fecha de inicio es requerida';
    if (!form.endDate) errors.endDate = 'La fecha de fin es requerida';
    if (form.startDate && form.endDate && new Date(form.startDate) >= new Date(form.endDate)) {
      errors.endDate = 'La fecha de fin debe ser posterior a la de inicio';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !agentId || !token) return;
    setSaving(true);
    try {
      if (editingId) {
        const payload = {
          customerName: form.customerName.trim(),
          customerEmail: form.customerEmail.trim() || null,
          customerPhone: form.customerPhone.trim() || null,
          startDate: new Date(form.startDate).toISOString(),
          endDate: new Date(form.endDate).toISOString(),
          timeBlock: form.timeBlock.trim() || null,
          status: form.status,
        };
        await apiPut(
          `/api/agents/${agentId}/calendar/bookings/${editingId}`,
          payload,
          token,
          { successMessage: 'Reserva actualizada' }
        );
      } else {
        const payload = {
          resourceId: form.resourceId,
          customerName: form.customerName.trim(),
          customerEmail: form.customerEmail.trim() || null,
          customerPhone: form.customerPhone.trim() || null,
          startDate: new Date(form.startDate).toISOString(),
          endDate: new Date(form.endDate).toISOString(),
          timeBlock: form.timeBlock.trim() || null,
          status: form.status,
          createdBy: form.createdBy,
        };
        await apiPost(
          `/api/agents/${agentId}/calendar/bookings`,
          payload,
          token,
          { successMessage: 'Reserva creada' }
        );
      }
      closeModal();
      await loadBookings();
    } catch (e) {
      setFormErrors({ _general: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  // ─── Cancel Handler ──────────────────────────────────────────────────────

  const handleCancel = async () => {
    if (!cancelId || !agentId || !token) return;
    setCancelling(true);
    try {
      await apiDeleteWithBody(
        `/api/agents/${agentId}/calendar/bookings/${cancelId}`,
        { reason: cancelReason.trim() || undefined },
        token
      );
      setCancelId(null);
      setCancelReason('');
      await loadBookings();
    } catch (e) {
      setError((e as Error).message);
      setCancelId(null);
      setCancelReason('');
    } finally {
      setCancelling(false);
    }
  };

  // ─── Confirm Payment Handler ─────────────────────────────────────────────

  const handleConfirmPayment = async (bookingId: string) => {
    if (!agentId || !token) return;
    try {
      await apiPost(
        `/api/agents/${agentId}/calendar/bookings/${bookingId}/confirm`,
        {},
        token
      );
      await loadBookings();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // ─── Detail/Changelog ────────────────────────────────────────────────────

  const openDetail = async (booking: Booking) => {
    // Fetch full booking with changelog
    if (!agentId || !token) return;
    try {
      const full = await apiGet<Booking>(
        `/api/agents/${agentId}/calendar/bookings/${booking.id}`,
        token
      );
      setDetailBooking(full);
    } catch {
      setDetailBooking(booking);
    }
  };

  // ─── Pagination ──────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / pageSize);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!token) return null;

  return (
    <div style={{ maxWidth: 1060, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 12 }}>
          <Link
            to={`/app/agents/${agentId}/calendar/resources`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              color: tokens.colors.neutral[600],
              textDecoration: 'none',
              fontSize: 14,
              transition: `color ${tokens.transitions.fast}`,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
            onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
          >
            <IconArrowLeft />
            Volver a recursos
          </Link>
          <Link
            to={`/app/agents/${agentId}/calendar/resource-types`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: tokens.colors.neutral[600],
              textDecoration: 'none',
              fontSize: 14,
              transition: `color ${tokens.transitions.fast}`,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
            onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
          >
            Tipos de recurso y estados de reserva
          </Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tokens.borderRadius.lg,
                backgroundColor: tokens.colors.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colors.primary[600],
              }}
            >
              <IconCalendar />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
                Reservas
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                Gestiona las reservas del calendario del agente
              </p>
            </div>
          </div>
          <Button variant="primary" size="md" onClick={openCreateModal}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconPlus /> Crear Reserva
            </span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 160px', minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 12, color: tokens.colors.neutral[500], marginBottom: 4 }}>Recurso</label>
          <select
            value={filterResourceId}
            onChange={(e) => setFilterResourceId(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.neutral[300]}`,
              backgroundColor: tokens.colors.neutral[50],
              color: tokens.colors.neutral[900],
              outline: 'none',
            }}
          >
            <option value="">Todos los recursos</option>
            {resources.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: '1 1 140px', minWidth: 120 }}>
          <label style={{ display: 'block', fontSize: 12, color: tokens.colors.neutral[500], marginBottom: 4 }}>Estado</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.neutral[300]}`,
              backgroundColor: tokens.colors.neutral[50],
              color: tokens.colors.neutral[900],
              outline: 'none',
            }}
          >
            <option value="">Todos</option>
            <option value="pending_deposit">Pendiente de seña</option>
            <option value="confirmed">Confirmada</option>
            <option value="human_supervision">Supervisión humana</option>
            <option value="cancelled">Cancelada</option>
            <option value="auto_cancelled">Cancelada automáticamente</option>
          </select>
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 12, color: tokens.colors.neutral[500], marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.neutral[300]}`,
              backgroundColor: tokens.colors.neutral[50],
              color: tokens.colors.neutral[900],
              outline: 'none',
            }}
          />
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 12, color: tokens.colors.neutral[500], marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.neutral[300]}`,
              backgroundColor: tokens.colors.neutral[50],
              color: tokens.colors.neutral[900],
              outline: 'none',
            }}
          />
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 180 }}>
          <label style={{ display: 'block', fontSize: 12, color: tokens.colors.neutral[500], marginBottom: 4 }}>
            Buscar (cliente o código)
          </label>
          <Input
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            placeholder="Nombre o código p. ej. ABC042"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: tokens.colors.neutral[500] }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}>
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando reservas...
        </div>
      )}

      {/* Empty state */}
      {!loading && bookings.length === 0 && !error && (
        <Card variant="outlined" padding="lg">
          <div style={{ textAlign: 'center', color: tokens.colors.neutral[500], padding: 24 }}>
            <IconCalendar />
            <p style={{ marginTop: 12, marginBottom: 16 }}>No hay reservas registradas.</p>
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              Crear primera reserva
            </Button>
          </div>
        </Card>
      )}

      {/* Bookings List */}
      {!loading && bookings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bookings.map((booking) => (
            <div
              key={booking.id}
              id={`booking-row-${booking.id}`}
              style={
                highlightBookingId === booking.id
                  ? {
                      borderRadius: 12,
                      boxShadow: `0 0 0 2px ${tokens.colors.accent[500]}`,
                    }
                  : undefined
              }
            >
            <Card variant="outlined" padding="md">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: airbnbNameWeight(booking), color: tokens.colors.neutral[900] }}>
                      {airbnbNameLabel(booking)}
                    </h3>
                    <Badge variant={statusBadgeVariant(booking.status)} size="sm">
                      {statusDisplayLabel(booking.status, booking.resource ?? null)}
                    </Badge>
                    {getCountdownText(booking) && (
                      <span style={{ fontSize: 12, color: tokens.colors.warning.dark, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {getCountdownText(booking)}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 13, color: tokens.colors.neutral[500], flexWrap: 'wrap' }}>
                    <span>Recurso: {booking.resource?.name || '—'}</span>
                    <span>{formatDateTime(booking.startDate)} – {formatDateTime(booking.endDate)}</span>
                    {booking.timeBlock && <span>Bloque: {booking.timeBlock}</span>}
                    {booking.publicReference ? (
                      <span style={{ fontWeight: 600, color: tokens.colors.neutral[700] }}>
                        Código: {booking.publicReference}
                      </span>
                    ) : null}
                    <span>Creado por: {booking.createdBy === 'operator' ? 'Operador' : 'Visitante'}</span>
                    {booking.conversationId ? (
                      <Link
                        to={bookingChatPath(
                          booking.conversationId,
                          booking.sourceMessageId ?? booking.sourceMessage?.id
                        )}
                        style={{ fontWeight: 600, color: tokens.colors.primary[600] }}
                      >
                        Ver mensaje del chat
                      </Link>
                    ) : null}
                  </div>
                  {booking.conversationId && chatMessagePreview(booking.sourceMessage?.content) ? (
                    <div style={{ marginTop: 6, fontSize: 13, color: tokens.colors.neutral[600] }}>
                      “{chatMessagePreview(booking.sourceMessage?.content)}”
                    </div>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {booking.status === 'pending_deposit' && (
                    <Button variant="primary" size="sm" onClick={() => handleConfirmPayment(booking.id)}>
                      Confirmar Pago
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openDetail(booking)} aria-label="Ver detalle">
                    <IconEye />
                  </Button>
                  {booking.status !== 'cancelled' && booking.status !== 'auto_cancelled' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(booking)} aria-label="Editar">
                        <IconEdit />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setCancelId(booking.id)} aria-label="Cancelar">
                        {booking.createdBy === 'visitor' ? 'Cancelar' : <IconTrash />}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
            </div>
          ))}

          {/* Pagination */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <span style={{ fontSize: 13, color: tokens.colors.neutral[500] }}>
              {total} reserva{total !== 1 ? 's' : ''} en total — Página {page} de {totalPages || 1}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => setPage((p) => p - 1)} disabled={!canPrev}>
                <IconChevronLeft />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
                <IconChevronRight />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Editar Reserva' : 'Crear Reserva'}
        size="lg"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={closeModal} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {formErrors._general && (
            <div style={{ padding: '8px 12px', borderRadius: tokens.borderRadius.md, backgroundColor: tokens.colors.error.light, color: tokens.colors.error.dark, fontSize: 13 }}>
              {formErrors._general}
            </div>
          )}

          {/* Resource */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Recurso *
            </label>
            <select
              value={form.resourceId}
              onChange={(e) => setForm({ ...form, resourceId: e.target.value })}
              disabled={!!editingId}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${formErrors.resourceId ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                backgroundColor: editingId ? tokens.colors.neutral[100] : tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
              }}
            >
              <option value="">Seleccionar recurso...</option>
              {resources.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {formErrors.resourceId && (
              <div style={{ fontSize: 12, color: tokens.colors.error.dark, marginTop: 4 }}>{formErrors.resourceId}</div>
            )}
          </div>

          {/* Customer Name */}
          <Input
            label="Nombre del Cliente *"
            placeholder="Nombre completo"
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            error={formErrors.customerName}
            fullWidth
          />

          {/* Customer Email & Phone */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Input
              label="Email (opcional)"
              type="email"
              placeholder="cliente@email.com"
              value={form.customerEmail}
              onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
              fullWidth
            />
            <Input
              label="Teléfono (opcional)"
              type="tel"
              placeholder="+598 99 123 456"
              value={form.customerPhone}
              onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
              fullWidth
            />
          </div>

          {/* Start/End Date */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                Fecha Inicio *
              </label>
              <input
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${formErrors.startDate ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                  backgroundColor: tokens.colors.neutral[50],
                  color: tokens.colors.neutral[900],
                  outline: 'none',
                }}
              />
              {formErrors.startDate && (
                <div style={{ fontSize: 12, color: tokens.colors.error.dark, marginTop: 4 }}>{formErrors.startDate}</div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                Fecha Fin *
              </label>
              <input
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${formErrors.endDate ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                  backgroundColor: tokens.colors.neutral[50],
                  color: tokens.colors.neutral[900],
                  outline: 'none',
                }}
              />
              {formErrors.endDate && (
                <div style={{ fontSize: 12, color: tokens.colors.error.dark, marginTop: 4 }}>{formErrors.endDate}</div>
              )}
            </div>
          </div>

          {/* Time Block */}
          <Input
            label="Bloque Horario (opcional)"
            placeholder="Ej: Mañana, Tarde, Noche"
            value={form.timeBlock}
            onChange={(e) => setForm({ ...form, timeBlock: e.target.value })}
            helperText="Solo para recursos con modo bloque horario"
            fullWidth
          />

          {/* Status */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Estado
            </label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as BookingFormData['status'] })}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.neutral[300]}`,
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
              }}
            >
              {statusModalChoices.map((s) => (
                <option key={s} value={s}>
                  {statusDisplayLabel(s, resourceForStatusLabels)}
                </option>
              ))}
            </select>
          </div>

          {/* Created By (only on create) */}
          {!editingId && (
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                Creado por
              </label>
              <select
                value={form.createdBy}
                onChange={(e) => setForm({ ...form, createdBy: e.target.value as 'visitor' | 'operator' })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${tokens.colors.neutral[300]}`,
                  backgroundColor: tokens.colors.neutral[50],
                  color: tokens.colors.neutral[900],
                  outline: 'none',
                }}
              >
                <option value="operator">Operador</option>
                <option value="visitor">Visitante</option>
              </select>
            </div>
          )}
        </div>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={!!cancelId}
        onClose={() => { setCancelId(null); setCancelReason(''); }}
        title="Cancelar Reserva"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={() => { setCancelId(null); setCancelReason(''); }} disabled={cancelling}>
              Volver
            </Button>
            <Button variant="primary" size="md" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelando...' : 'Confirmar Cancelación'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, color: tokens.colors.neutral[700] }}>
            ¿Estás seguro de que deseas cancelar esta reserva?
          </p>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Motivo de cancelación (opcional)
            </label>
            <textarea
              placeholder="Ingresa el motivo..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.neutral[300]}`,
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Detail/Changelog Modal */}
      <Modal
        isOpen={!!detailBooking}
        onClose={() => setDetailBooking(null)}
        title="Detalle de Reserva"
        size="lg"
        footer={
          <Button variant="ghost" size="md" onClick={() => setDetailBooking(null)}>
            Cerrar
          </Button>
        }
      >
        {detailBooking && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Booking Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Cliente</span>
                <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900], fontWeight: airbnbNameWeight(detailBooking) }}>
                  {airbnbNameLabel(detailBooking)}
                </p>
              </div>
              <div>
                <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Estado</span>
                <p style={{ margin: '2px 0 0' }}>
                  <Badge variant={statusBadgeVariant(detailBooking.status)} size="sm">
                    {statusDisplayLabel(detailBooking.status, detailBooking.resource ?? null)}
                  </Badge>
                </p>
              </div>
              {getCountdownText(detailBooking) ? (
                <div>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Seña</span>
                  <p
                    style={{
                      margin: '2px 0 0',
                      fontSize: 16,
                      color: tokens.colors.warning.dark,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {getCountdownText(detailBooking)}
                  </p>
                </div>
              ) : null}
              {detailBooking.publicReference ? (
                <div>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Código de reserva</span>
                  <p
                    style={{
                      margin: '2px 0 0',
                      fontSize: 15,
                      color: tokens.colors.neutral[900],
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      fontFamily: 'ui-monospace, monospace',
                    }}
                  >
                    {detailBooking.publicReference}
                  </p>
                </div>
              ) : null}
              <div>
                <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Recurso</span>
                <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                  {detailBooking.resource?.name || '—'}
                </p>
              </div>
              <div>
                <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Creado por</span>
                <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                  {detailBooking.createdBy === 'operator' ? 'Operador' : 'Visitante'}
                </p>
              </div>
              {detailBooking.conversationId ? (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Mensaje del chat</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                    {chatMessagePreview(detailBooking.sourceMessage?.content) ? (
                      <span>“{chatMessagePreview(detailBooking.sourceMessage?.content)}” · </span>
                    ) : null}
                    <Link
                      to={bookingChatPath(
                        detailBooking.conversationId,
                        detailBooking.sourceMessageId ?? detailBooking.sourceMessage?.id
                      )}
                      style={{ fontWeight: 600, color: tokens.colors.primary[600] }}
                    >
                      Abrir en la conversación
                    </Link>
                  </p>
                </div>
              ) : null}
              <div>
                <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Inicio</span>
                <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                  {formatDateTime(detailBooking.startDate)}
                </p>
              </div>
              <div>
                <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Fin</span>
                <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                  {formatDateTime(detailBooking.endDate)}
                </p>
              </div>
              {detailBooking.customerEmail && (
                <div>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Email</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                    {detailBooking.customerEmail}
                  </p>
                </div>
              )}
              {detailBooking.customerPhone && (
                <div>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Teléfono</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                    {detailBooking.customerPhone}
                  </p>
                </div>
              )}
              {detailBooking.timeBlock && (
                <div>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Bloque</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.neutral[900] }}>
                    {detailBooking.timeBlock}
                  </p>
                </div>
              )}
              {detailBooking.cancellationReason && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>Motivo de cancelación</span>
                  <p style={{ margin: '2px 0 0', fontSize: 14, color: tokens.colors.error.dark }}>
                    {detailBooking.cancellationReason}
                  </p>
                </div>
              )}
              {detailBooking.depositReceiptUrl ? (
                <BookingDepositReceiptBlock rawUrl={detailBooking.depositReceiptUrl} />
              ) : null}
            </div>

            {/* Changelog */}
            <div style={{ borderTop: `1px solid ${tokens.colors.neutral[200]}`, paddingTop: 16 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: tokens.colors.neutral[700] }}>
                Historial de Cambios
              </h4>
              {(!detailBooking.changelog || (Array.isArray(detailBooking.changelog) && detailBooking.changelog.length === 0)) && (
                <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[400] }}>Sin cambios registrados.</p>
              )}
              {Array.isArray(detailBooking.changelog) && detailBooking.changelog.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {detailBooking.changelog.map((entry, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '8px 12px',
                        borderRadius: tokens.borderRadius.md,
                        backgroundColor: tokens.colors.neutral[50],
                        border: `1px solid ${tokens.colors.neutral[200]}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <Badge
                          variant={
                            entry.action === 'cancelled' || entry.action === 'auto_cancelled'
                              ? 'error'
                              : entry.action === 'created'
                              ? 'success'
                              : entry.action === 'confirmed'
                              ? 'success'
                              : entry.action === 'receipt_attached'
                              ? 'info'
                              : 'neutral'
                          }
                          size="sm"
                        >
                          {entry.action === 'created' ? 'Creada'
                            : entry.action === 'modified' ? 'Modificada'
                            : entry.action === 'cancelled' ? 'Cancelada'
                            : entry.action === 'auto_cancelled' ? 'Auto-cancelada'
                            : entry.action === 'confirmed' ? 'Confirmada'
                            : entry.action === 'receipt_attached' ? 'Comprobante adjunto'
                            : entry.action}
                        </Badge>
                        <span style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>
                          {formatDateShort(entry.timestamp)}
                        </span>
                        {entry.operatorId && (
                          <span style={{ fontSize: 12, color: tokens.colors.neutral[400] }}>
                            por {entry.operatorId}
                          </span>
                        )}
                      </div>
                      {entry.changes && Object.keys(entry.changes).length > 0 && (
                        <div style={{ fontSize: 12, color: tokens.colors.neutral[600], marginTop: 4 }}>
                          {Object.entries(entry.changes).map(([field, change]) => (
                            <div key={field}>
                              <strong>{field}</strong>: {String(change.from)} → {String(change.to)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default CalendarBookingsPage;
